import { useEffect, useRef, useState } from "react";
import * as PropTypes from "prop-types";
import { useMediaQuery, useTheme, Alert, AlertTitle, Box, Button } from "@mui/material";
import { useTranslation } from "react-i18next";

export default function AppPopupWindow(props) {
  const { src, title, show, setShow, width = 500, height = 700 } = props;
  const popupRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();
  const [error, setError] = useState(null);

  useEffect(() => {
    if (show && src) {
      setError(null); // Clear any previous errors
      
      if (isMobile) {
        // Mobile web browser - open in new tab
        const newTab = window.open(src, '_blank');
        if (newTab) {
          // Focus the new tab
          newTab.focus();
          // Close the modal immediately since we can't detect when user closes the tab
          setShow(false);
        } else {
          // Popup was blocked - show error message
          setError('popupBlocked');
        }
      } else {
        // Desktop browser - use popup window
        const left = (window.screen.width - width) / 2;
        const top = (window.screen.height - height) / 2;

        const popup = window.open(
          src,
          title || 'Popup Window',
          `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes,toolbar=no,menubar=no,location=no,status=no`
        );

        if (popup) {
          popupRef.current = popup;
          popup.focus();

          // Handle popup close detection
          const checkClosed = setInterval(() => {
            if (popup.closed) {
              clearInterval(checkClosed);
              setShow(false);
            }
          }, 1000);

          return () => {
            clearInterval(checkClosed);
            if (popup && !popup.closed) {
              popup.close();
            }
          };
        } else {
          // Popup was blocked - show error message
          setError('popupBlocked');
        }
      }
    }
  }, [show, src, title, width, height, setShow, isMobile]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (popupRef.current && !popupRef.current.closed) {
        popupRef.current.close();
      }
    };
  }, []);

  const handleRetry = () => {
    setError(null);
    // Trigger the useEffect again
    if (show && src) {
      if (isMobile) {
        const newTab = window.open(src, '_blank');
        if (newTab) {
          newTab.focus();
          setShow(false);
        } else {
          setError('popupBlocked');
        }
      } else {
        const left = (window.screen.width - width) / 2;
        const top = (window.screen.height - height) / 2;
        const popup = window.open(
          src,
          title || 'Popup Window',
          `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes,toolbar=no,menubar=no,location=no,status=no`
        );
        if (popup) {
          popupRef.current = popup;
          popup.focus();
          setShow(false);
        } else {
          setError('popupBlocked');
        }
      }
    }
  };

  const handleClose = () => {
    setError(null);
    setShow(false);
  };

  // Show error message if popup was blocked
  if (error === 'popupBlocked') {
    return (
      <Box sx={{ p: 3, maxWidth: 400, mx: 'auto' }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>
            {isMobile ? t('popup.mobileBlockedTitle', 'Popup Blocked') : t('popup.desktopBlockedTitle', 'Popup Blocked')}
          </AlertTitle>
          {isMobile 
            ? t('popup.mobileBlockedMessage', 'Your browser blocked the new tab. Please allow popups for this site and try again.')
            : t('popup.desktopBlockedMessage', 'Your browser blocked the popup window. Please allow popups for this site and try again.')
          }
        </Alert>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
          <Button variant="outlined" onClick={handleRetry}>
            {t('popup.retry', 'Try Again')}
          </Button>
          <Button variant="contained" onClick={handleClose}>
            {t('popup.close', 'Close')}
          </Button>
        </Box>
      </Box>
    );
  }

  // This component doesn't render anything visible when working normally
  // The popup window is handled by the useEffect
  return null;
}

AppPopupWindow.propTypes = {
  src: PropTypes.string.isRequired,
  title: PropTypes.string,
  show: PropTypes.bool.isRequired,
  setShow: PropTypes.func.isRequired,
  width: PropTypes.number,
  height: PropTypes.number,
};
